<template>
    <hox-modal v-if="!isPermissionGranted">
        <hox-alert margin-top="base" type="danger" @close="redirectToClients">
            <template #content>
                <p>It looks like you do not have the right permissions to see the library for this campaign.</p>
                <p>Ask your admin to grant you access and try again.</p>
            </template>
            <template #actionItems>
                <Button type="primary" @click="redirectToClients">Got it</Button>
            </template>
        </hox-alert>
    </hox-modal>
    <div v-else class="campaign-library__wrapper">
        <DeliverableDrawer
            v-if="drawerIsVisible && !isLoading"
            :deliverables="drawerDeliverables"
            @closed="onDrawerClosed"
            @statusHasBeenUpdated="onDeliverableStatusUpdate"
            @userStatusHasBeenUpdated="onDeliverableUserStatusUpdate"
            @annotationsHasBeenUpdated="onDeliverableCommentsUpdate"
        />

        <MultiShareModal
            v-if="sharePreviewModalIsVisible"
            :campaign-id="campaignId"
            :deliverables="selectedDeliverables"
            @close="toggleSharePreviewModal"
        />

        <DownloadModal
            v-if="downloadModalIsVisible"
            :deliverables="selectedDeliverables"
            :deliverable-identifiers="selectedDeliverableIdentifiers"
            :download-deliverables="downloadDeliverables"
            :is-import-download="isImportDownload"
            @close="toggleDownloadModal"
        />

        <PublishModal
            v-if="publishModalIsVisible"
            :deliverables="selectedDeliverables"
            :action="publishModalData.action!"
            :platform="publishModalData.platform!"
            @close="togglePublishModal"
        />

        <ImportExportLibraryModal
            v-if="importExportModalVisible"
            v-model:visible="importExportModalVisible"
            :is-download-deliveries-loading="isDownloadDeliveriesLoading"
            @exportTemplate="onExportTemplate"
            @applySelection="applySelectionFromFile"
            @download="downloadDeliverableIdsFromFile"
            @close="toggleImportExportModalVisible"
        />

        <div class="campaign-library">
            <hox-loading-layer v-if="isLoading" is-opaque :left-offset-pixels="60" size="large" />
            <div v-if="hasErrorLoadingRequiredData" class="campaign-library__loading-error-message-wrapper">
                <hox-alert margin-bottom="large" type="danger">
                    <template #title>Uh oh!</template>
                    <template #content>
                        <p>
                            There was an unexpected issue while loading library data. Hopefully it was a temporary issue
                            and should be fixed if you try again in a few moments.
                        </p>
                    </template>
                    <template #actionItems>
                        <Button type="primary" @click="getRequiredLibraryData">Retry</Button>
                    </template>
                </hox-alert>
            </div>
            <div v-else-if="!hasFilterOptionsForEachType">
                <hox-empty-message v-if="userCanAccessEditor">
                    <template #title>This campaign doesn't have any ads</template>
                    <template #content>
                        <p>Head over to the editor to get started</p>
                    </template>
                </hox-empty-message>
                <hox-empty-message v-else>
                    <template #title>There are no ads in the library</template>
                </hox-empty-message>
            </div>

            <template v-else>
                <LibraryHeader
                    :paginated-deliverables="paginatedDeliverables"
                    :total-items="pagination.totalItems"
                    :select-all-disabled="isLoadingDeliverables"
                    :selected-deliverables="selectedDeliverables"
                    :view-mode="viewMode"
                    @selectedDeliverablesUpdated="setSelectedDeliverableIds"
                    @viewModeSet="setViewMode"
                    @selectViaFile="toggleImportExportModalVisible"
                />

                <LibraryActionsHeader
                    :total-items="pagination.totalItems"
                    :selected-deliverables="selectedDeliverables"
                    :is-last-job-complete="isLastJobComplete"
                    :last-job="lastJob"
                    :campaign-feed="campaignFeed"
                    @showSharePreviewModal="toggleSharePreviewModal"
                    @showBulkApproval="showBulkApproval"
                    @refetchDeliverables="refetchDeliverables"
                    @bulkQaStatusHasBeenSet="refetchDeliverablesIfFilteringByQaStatus"
                    @bulkPublishedStatusHasBeenSet="refetchDeliverablesIfFilteringByPublishedStatus"
                    @selectedDeliverablesUpdated="setSelectedDeliverableIds"
                    @viewModeSet="setViewMode"
                    @showDownloadModal="toggleDownloadModal"
                    @showPublishModal="togglePublishModal"
                />

                <div v-if="isPaginationNoticeShown" class="campaign-library__select-all-notice-wrapper">
                    <SelectAllNotice
                        :all-selected-mode="allSelectedModeEnabled"
                        :selected="selectedDeliverableIds.length"
                        :total-count="pagination.totalItems"
                        @dismiss="isPaginationNoticeShown = false"
                        @selectAll="onSelectAll"
                    />
                </div>

                <div
                    ref="body"
                    class="campaign-library__body"
                    :class="{
                        'campaign-library__body--no-padding': !isGridViewMode && !hasErrorLoadingDeliverables
                    }"
                >
                    <CampaignLockedNotice
                        :class="{ 'campaign-locked-notice--table-view': !isGridViewMode }"
                        @campaignUnlocked="campaignUnlocked"
                    />

                    <div v-if="!allFiltersAreActive" class="campaign-library__empty-message-wrapper">
                        <hox-empty-message>
                            <template #title>No deliverables to show</template>
                            <template #content>
                                <p>
                                    You need to select at least one <em>language</em>, <em>size</em> and
                                    <em>group value for every group filter</em> in the sidebar on the left.
                                </p>
                            </template>
                        </hox-empty-message>
                    </div>

                    <hox-alert v-else-if="hasErrorLoadingDeliverables" margin-bottom="large" type="danger">
                        <template #title>Uh oh!</template>
                        <template #content>
                            <p>
                                There was an unexpected issue while getting the deliverables. Hopefully it was a
                                temporary issue and should be fixed if you try again in a few moments.
                            </p>
                        </template>
                        <template #actionItems>
                            <Button type="primary" @click="getPaginatedDeliverables">Retry getting deliverables</Button>
                        </template>
                    </hox-alert>

                    <template v-else-if="!isGridViewMode">
                        <LibraryDeliverableTable
                            :all-selected-mode="allSelectedModeEnabled"
                            :deliverables="paginatedDeliverables"
                            :is-loading-deliverables="isLoadingDeliverables"
                            :selected-deliverables="selectedDeliverables"
                            :total-items="pagination.totalItems"
                            :selected-deliverable-ids="selectedDeliverableIds"
                            :is-route-changing="isRouteChanging"
                            @selectedDeliverablesChanged="setSelectedDeliverableIds"
                            @reportingLabelUpdatedOnDeliverable="updateReportingLabelOnDeliverable"
                            @metadataValueUpdatedOnDeliverables="
                                ({ idHashes, metadataValue: updatedMetadataValue }) => {
                                    updateMetadataValueOnDeliverables(idHashes, updatedMetadataValue);
                                }
                            "
                        >
                            <template #emptyMessage>
                                <LibraryEmptyMessage />
                            </template>
                        </LibraryDeliverableTable>
                    </template>

                    <div
                        v-else-if="!paginatedDeliverables.length && !isLoadingDeliverables"
                        class="campaign-library__empty-message-wrapper"
                    >
                        <LibraryEmptyMessage />
                    </div>

                    <template v-else>
                        <CheckboxGroup
                            v-if="isGridViewMode"
                            v-model="checkboxSelectedDeliverableIds"
                            class="library-previews-container"
                        >
                            <div
                                v-for="size in Object.keys(deliverableSizeGroups)"
                                :key="size"
                                :class="[
                                    'campaign-edit__deliverables-size-group',
                                    deliverableSizeGroups[size].length === 1
                                        ? 'campaign-edit__deliverables-size-group--single'
                                        : ''
                                ]"
                            >
                                <LibraryPreview
                                    v-for="deliverable in deliverableSizeGroups[size]"
                                    :key="deliverable.idHash"
                                    class="campaign-edit__deliverable"
                                    :deliverable="deliverable"
                                    :max-height="bodyDimensionsPixels.height"
                                    :max-width="bodyDimensionsPixels.width"
                                    :scaling-factor="scalingFactor"
                                    :playback-namespace="playbackNamespace"
                                    @statusHasBeenUpdated="refetchDeliverablesIfFilteringByQaStatus"
                                />
                            </div>
                        </CheckboxGroup>

                        <hox-loading-layer v-if="isLoadingDeliverables" :is-full-screen="false" />
                    </template>
                </div>

                <template v-if="paginatedDeliverables.length || hasErrorLoadingDeliverables || isLoadingDeliverables">
                    <PreviewGroupControls
                        v-if="isGridViewMode && playbackController"
                        :key="pagination.totalPages"
                        :scaling="{ scalingFactor }"
                        :scrubbar="{
                            currentTime: playbackController.currentTime.value,
                            duration: playbackController.duration.value
                        }"
                        replay-button
                        :pagination="{
                            currentPage: pagination.currentPage,
                            pageSize: pagination.pageSize,
                            total: pagination.totalItems
                        }"
                        @replay="onReplayPreviews"
                        @setScalingFactor="setScalingFactor"
                        @setBannerPlaybackTime="playbackController.seek"
                        @setPage="setCurrentPage"
                    />

                    <PreviewGroupControls
                        v-else
                        :pagination="{
                            currentPage: pagination.currentPage,
                            pageSize: pagination.pageSize,
                            total: pagination.totalItems
                        }"
                        :page-size-controls="{ pageSize: pagination.pageSize, options: pageSizeOptions }"
                        @setPage="setCurrentPage"
                        @setPageSize="setPageSize"
                    />
                </template>
            </template>

            <sidebar :scroll="false">
                <div v-if="hasFilterOptionsForEachType" class="campaign-library__sidebar campaign-light__sidebar">
                    <nav class="campaign-library__sidebar-nav campaign-light__sidebar-nav">
                        <ButtonGroup class="campaign-library__sidebar-nav-buttons-group">
                            <Button
                                size="small"
                                class="campaign-library__sidebar-nav-button"
                                data-testid="campaign-library__sidebar-nav-button-creatives"
                                :type="filtersTab === 'creatives' ? 'primary' : 'default'"
                                @click="filtersTab = 'creatives'"
                            >
                                Creatives
                            </Button>
                            <Button
                                size="small"
                                class="campaign-library__sidebar-nav-button"
                                data-testid="campaign-library__sidebar-nav-button-workflow"
                                :type="filtersTab === 'workflow' ? 'primary' : 'default'"
                                @click="filtersTab = 'workflow'"
                            >
                                Workflow
                            </Button>
                        </ButtonGroup>
                    </nav>

                    <div class="campaign-library__variations">
                        Showing
                        <hox-loading-spinner v-if="isLoadingDeliverables" />
                        <template v-else>
                            {{ totalGeneratedVariations }}
                        </template>
                        of {{ totalVariations }} variations
                    </div>

                    <div v-show="filtersTab === 'creatives'">
                        <LibraryLanguageFilter />
                        <LibrarySizeFilter />
                        <EditableGroupCheckboxList />
                    </div>

                    <div v-show="filtersTab === 'workflow'">
                        <LibraryCreativeXFilter v-if="hasCreativeXEnabled" />
                        <LibrarySataliaFilter v-if="hasSataliaEnabled" />
                        <LibraryQaCommentedOnFilter />
                        <LibraryQaStatusFilter />
                        <LibraryPublishedStatusFilter />
                        <LibraryQaUserStatusFilter />
                        <LibraryVisibleToFilter
                            v-if="$auth.userCan($auth.Actions.CanChangeDeliverableVisibility, { clientId, campaignId })"
                        />
                        <LibraryMiscFilter />
                    </div>
                </div>
            </sidebar>
        </div>
    </div>
</template>

<script lang="ts">
import { ref, defineComponent, markRaw } from "vue";
import { type NavigationGuardNext, type RouteLocationNormalized } from "vue-router";
import { mapStores } from "pinia";

import type { MasterTemplate, EditableWithOverwrites, MasterTemplateOptions, EditableGroupValue } from "hoxton-types";

import type { State as DeliverableLibraryState } from "@/store/modules/deliverableLibrary";

import type { ConstructedDeliverable } from "@/types/types";

import { CampaignAction, CampaignGetters } from "@/store/modules/campaign";
import { DeliverableLibraryAction } from "@/store/modules/deliverableLibrary";
import { formatGroupName, generateTemplateLabel, numberWithCommas } from "@/utils";
import { QueryParamPrefix } from "@/utils/urls";

import { BannerNamespace, usePlaybackController } from "@/components/PlaybackControls/usePlaybackController";

import { getMetadataFieldsOfCampaign } from "@/services/Metadata";
import URLManager from "@/services/URLManager";

import campaignEditorQuery from "@/apollo/queries/v2/CampaignEditor.gql";
import campaignLibraryQuery from "@/apollo/queries/v2/CampaignLibrary.gql";
import computedDeliverableComponentsQuery from "@/apollo/queries/v2/ComputedDeliverableComponents.gql";
import lastJobByCampaignId from "@/apollo/queries/v2/LastJobByCampaignId.gql";

import EditableGroupCheckboxList from "@/components/Campaign/EditableGroupCheckboxList.vue";
import LibraryActionsHeader from "@/components/Campaign/LibraryActionsHeader.vue";
import LibraryCreativeXFilter from "@/components/Campaign/LibraryCreativeXFilter.vue";
import LibrarySataliaFilter from "@/components/Campaign/LibrarySataliaFilter.vue";
import LibraryDeliverableTable from "@/components/Campaign/LibraryDeliverableTable/LibraryDeliverableTable.vue";
import LibraryHeader from "@/components/Campaign/LibraryHeader.vue";
import LibraryLanguageFilter from "@/components/Campaign/LibraryLanguageFilter.vue";
import LibraryPreview from "@/components/Campaign/LibraryPreview.vue";
import LibraryPublishedStatusFilter from "@/components/Campaign/LibraryPublishedStatusFilter.vue";
import LibraryQaStatusFilter from "@/components/Campaign/LibraryQaStatusFilter.vue";
import LibrarySizeFilter from "@/components/Campaign/LibrarySizeFilter.vue";
import PublishModal from "@/components/Campaign/Publish/PublishModal.vue";
import DownloadModal from "@/components/Download/DownloadModal.vue";
import MultiShareModal from "@/components/MultiShareModal.vue";
import Sidebar from "@/components/Sidebar.vue";

import { JobNames, JobStatus } from "@/enums/jobs";
import {
    LibraryDrawerTab,
    UrlFilterParams,
    ViewMode,
    ViewModeDefaultPageSize,
    type DeliverableIdentifier
} from "@/enums/library";
import {
    BannerQaCommentedOnByFilter,
    BannerQaStatusFilter,
    BannerQaUserStatusFilter,
    CreativeXScoreFilters,
    PreflightStatusFilters,
    PublishedStatusFilter
} from "@/enums/filters";
import { PlatformLabel } from "@/enums/platforms";

import DeliverableDrawer from "@/components/Campaign/DeliverableDrawer.vue";
import ImportExportLibraryModal from "@/components/Campaign/ImportExportLibraryModal.vue";
import LibraryEmptyMessage from "@/components/Campaign/LibraryEmptyMessage.vue";
import LibraryMiscFilter from "@/components/Campaign/LibraryMiscFilter.vue";
import LibraryQaCommentedOnFilter from "@/components/Campaign/LibraryQaCommentedOnFilter.vue";
import LibraryQaUserStatusFilter from "@/components/Campaign/LibraryQaUserStatusFilter.vue";
import LibraryVisibleToFilter from "@/components/Campaign/LibraryVisibleToFilter.vue";
import SelectAllNotice from "@/components/Campaign/SelectAllNotice.vue";
import PreviewGroupControls from "@/components/PlaybackControls/PreviewGroupControls.vue";
import { useScalingBannerWrapper } from "@/composables/useScalingBannerWrapper";

import { UiAction } from "@/store/modules/ui";
import getPaginatedDeliverables from "@/services/deliverables/getPaginatedDeliverables";
import ImportExport from "@/services/ImportExport";
import CampaignLockedNotice from "@/views/Campaign/CampaignLockedNotice.vue";
import { useCreativeLibraryFiltersStore } from "@/store/pinia/creativeLibraryFilters";
import { AppRouteNames } from "@/router/constants";

import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";

interface DeliverableMetadataValue {
    _id: string;
    field: {
        _id: string;
        name: string;
    };
    value: string;
}

export default defineComponent({
    name: "CampaignLibrary",
    components: {
        CampaignLockedNotice,
        LibraryMiscFilter,
        ImportExportLibraryModal,
        LibraryQaCommentedOnFilter,
        LibraryQaUserStatusFilter,
        LibraryEmptyMessage,
        LibraryVisibleToFilter,
        SelectAllNotice,
        PreviewGroupControls,
        LibraryPreview,
        LibraryDeliverableTable,
        DeliverableDrawer,
        LibraryHeader,
        LibraryActionsHeader,
        LibraryLanguageFilter,
        LibraryPublishedStatusFilter,
        LibraryQaStatusFilter,
        LibrarySizeFilter,
        EditableGroupCheckboxList,
        MultiShareModal,
        DownloadModal,
        PublishModal,
        Sidebar,
        LibraryCreativeXFilter,
        LibrarySataliaFilter
    },

    beforeRouteLeave(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
        this.isRouteChanging = true;
        next();
    },

    setup() {
        const body = ref(null);
        const { bodyDimensionsPixels, scalingFactor, setBodyDimensionsPixels } = useScalingBannerWrapper({
            bodyRef: body
        });

        const playbackNamespace = BannerNamespace.Library;
        const playbackController = usePlaybackController(playbackNamespace);

        const ga4 = useGA4Tracking();

        return {
            body,
            bodyDimensionsPixels,
            scalingFactor,
            setBodyDimensionsPixels,

            playbackNamespace,
            playbackController,
            ga4
        };
    },

    data() {
        return {
            downloadModalIsVisible: false,
            drawerDeliverablesHadCommentsUpdated: false,
            drawerDeliverablesHadStatusUpdated: false,
            drawerDeliverablesHadUserStatusUpdated: false,
            editableOverwrites: [] as EditableWithOverwrites[],
            filtersTab: "creatives" as "creatives" | "workflow",
            getPaginatedDeliverablesTimeout: undefined as ReturnType<typeof setTimeout> | undefined,
            hasErrorLoadingCampaign: false,
            hasErrorLoadingDeliverables: false,
            hasErrorLoadingFilterOptions: false,
            hasErrorLoadingMetadataFields: false,
            hasInitialisedFilterParams: false,
            hasErrorLoadingLastCampaignJob: false,
            importExportModalVisible: false,
            isBeingDestroyed: false,
            isLoadingCampaign: 0,
            isLoadingDeliverables: false,
            isLoadingLibraryFilterOptions: 0,
            isLoadingMetadataFields: false,
            isLoadingLastCampaignJob: false,
            isPaginationNoticeShown: false,
            mounted: true,
            numberOfDeliverablesThatUserCanView: 0,
            pageSizeOptions: [
                {
                    value: 10,
                    label: "10/page"
                },
                {
                    value: 25,
                    label: "25/page"
                },
                {
                    value: 50,
                    label: "50/page"
                },
                {
                    value: 75,
                    label: "75/page"
                },
                {
                    value: 100,
                    label: "100/page"
                }
            ],
            paginatedDeliverables: [] as ConstructedDeliverable[],
            paginatedDeliverablesRequestCounter: 0,
            publishModalData: {} as {
                action?: "publish" | "unpublish";
                platform?: keyof typeof PlatformLabel;
            },
            publishModalIsVisible: false,
            checkboxSelectedDeliverableIds: [] as string[],
            selectedDeliverableIds: [] as string[],
            selectedDeliverableIdentifiers: [] as string[],
            sharePreviewModalIsVisible: false,
            isRouteChanging: false,
            urlManager: null as URLManager | null,
            importExportService: null as ImportExport | null,
            isLastJobComplete: false,
            lastJob: null,
            campaignFeed: [],
            isDownloadDeliveriesLoading: false,
            downloadDeliverables: [] as ConstructedDeliverable[],
            isImportDownload: false
        };
    },

    computed: {
        ...mapStores(useCreativeLibraryFiltersStore),
        allFiltersAreActive() {
            const editableGroupsFilterOptions = this.$store.state.deliverableLibrary.filterOptions.editableGroups;

            const allGroupsHaveValues = !editableGroupsFilterOptions.some(groupFilterOption => {
                return !this.creativeLibraryFiltersStore.selectedEditableIdsByGroup[groupFilterOption.name];
            });

            const selectedLanguages = this.creativeLibraryFiltersStore.selectedLanguages;
            const selectedSizes = this.creativeLibraryFiltersStore.selectedSizes;

            return allGroupsHaveValues && selectedLanguages.length > 0 && selectedSizes.length > 0;
        },

        allSelectedModeEnabled() {
            return this.creativeLibraryFiltersStore.allSelectedMode;
        },

        campaignId() {
            return this.$route.params.campaignId as string;
        },

        canPreviewDeliverable() {
            return this.$auth.userCan(this.$auth.Actions.CanPreviewDeliverable, {
                clientId: this.clientId,
                campaignId: this.campaignId
            });
        },

        clientId() {
            return this.$route.params.clientId as string;
        },

        commentedOnByFilter() {
            return this.creativeLibraryFiltersStore.commentedOnBy;
        },

        completedJobs() {
            return this.$store.state.jobs.completedJobs;
        },

        userStatusFilter() {
            return this.creativeLibraryFiltersStore.reviewStatus;
        },

        isFilteringByUserStatus() {
            return !this.userStatusFilter.includes(BannerQaUserStatusFilter.All);
        },

        deliverablesByIdHash(): Record<string, ConstructedDeliverable> {
            return this.paginatedDeliverables.reduce<Record<string, ConstructedDeliverable>>((acc, deliverable) => {
                acc[deliverable.idHash] = {
                    ...deliverable
                };
                return acc;
            }, {});
        },

        deliverableSizeGroups() {
            return this.paginatedDeliverables.reduce<Record<string, ConstructedDeliverable[]>>((acc, deliverable) => {
                const size = `${deliverable.masterTemplate.width}x${deliverable.masterTemplate.height}`;
                acc[size] = (acc[size] ?? []).concat(deliverable);

                return acc;
            }, {});
        },

        drawerDeliverables: {
            get() {
                return this.$store.state.deliverableLibrary.drawer.deliverables;
            },
            set(value: ConstructedDeliverable[] | null) {
                this.$store.dispatch(DeliverableLibraryAction.SetDrawerDeliverables, value);
            }
        },

        drawerIsVisible: {
            get() {
                return this.$store.state.deliverableLibrary.drawer.isVisible;
            },
            set(value: boolean) {
                this.$store.dispatch(DeliverableLibraryAction.SetDrawerIsVisible, value);
            }
        },

        filterParams() {
            if (!this.$store.state.deliverableLibrary.campaignId) {
                return [];
            }

            const options = this.$store.state.deliverableLibrary.filterOptions;

            return this.creativeLibraryFiltersStore.getUrlFilterParams(options);
        },

        hasErrorLoadingRequiredData() {
            return (
                this.hasErrorLoadingCampaign ||
                this.hasErrorLoadingFilterOptions ||
                this.hasErrorLoadingMetadataFields ||
                this.hasErrorLoadingLastCampaignJob
            );
        },

        hasFilterOptionsForEachType() {
            const { filterOptions } = this.$store.state.deliverableLibrary;

            return (
                filterOptions.editableGroups.length > 0 &&
                filterOptions.languages.length > 0 &&
                filterOptions.masterTemplates.length > 0
            );
        },

        isFilteringByCommentedOnBy() {
            return this.commentedOnByFilter !== BannerQaCommentedOnByFilter.All;
        },

        isFilteringByPublishedStatus() {
            return this.publishedStatusFilter !== PublishedStatusFilter.All;
        },

        isFilteringByStatus() {
            return this.statusFilter !== BannerQaStatusFilter.All;
        },

        isGridViewMode() {
            return this.viewMode === ViewMode.Grid;
        },

        isInitializedForCampaign() {
            return this.libraryCampaignId && this.libraryCampaignId === this.campaignId;
        },

        isLoading() {
            return (
                this.isLoadingCampaign ||
                this.isLoadingLibraryFilterOptions ||
                this.isLoadingMetadataFields ||
                this.isLoadingLastCampaignJob
            );
        },

        isPermissionGranted() {
            return this.userHasScope && this.canPreviewDeliverable;
        },

        libraryCampaignId: {
            get() {
                return this.$store.state.deliverableLibrary.campaignId;
            },
            set(campaignId: string) {
                this.$store.dispatch(DeliverableLibraryAction.SetCampaignId, campaignId);
            }
        },

        pagination: {
            get() {
                return this.$store.state.deliverableLibrary.pagination;
            },
            set(newPagination: DeliverableLibraryState["pagination"]) {
                this.$store.dispatch(DeliverableLibraryAction.SetPagination, newPagination);
            }
        },

        publishedStatusFilter() {
            return this.creativeLibraryFiltersStore.publishedStatus;
        },

        selectedDeliverables() {
            return this.paginatedDeliverables.filter(deliverable => {
                return this.selectedDeliverableIds.includes(deliverable.idHash);
            });
        },

        sizeFilters(): string[] {
            return this.creativeLibraryFiltersStore.selectedSizes;
        },

        statusFilter() {
            return this.creativeLibraryFiltersStore.qaStatus;
        },

        totalGeneratedVariations() {
            if (this.allFiltersAreActive) {
                return numberWithCommas(this.pagination.totalItems);
            }
            return 0;
        },

        totalVariations() {
            return numberWithCommas(this.numberOfDeliverablesThatUserCanView);
        },

        userCanAccessEditor() {
            return this.$auth.userCan(this.$auth.Actions.CanManageOverwrites, {
                clientId: this.clientId,
                campaignId: this.campaignId
            });
        },

        userHasScope() {
            return this.$auth.hasScope({
                clientId: this.clientId,
                campaignId: this.campaignId
            });
        },

        viewMode: {
            get() {
                return this.$store.state.deliverableLibrary.viewMode;
            },
            set(newViewMode: string) {
                this.$store.dispatch(DeliverableLibraryAction.SetViewMode, newViewMode);
            }
        },

        hasCreativeXEnabled() {
            return this.$store.state.ui.currentClient?.creativeXPlatformSettings?.enabled ?? false;
        },

        hasSataliaEnabled() {
            return this.$store.state.ui.currentClient?.sataliaPlatformSettings?.enabled ?? false;
        },

        validGroupValuesNamesByGroupValue() {
            return this.$store.getters[CampaignGetters.groupValueNamesByGroup] as Record<
                string,
                {
                    name: string;
                    editables: string[];
                    editableGroupValuesNamesMap: Record<string, string>;
                    editableGroupValuesNames: string[];
                    editableGroupValues: string[];
                }
            >;
        }
    },

    watch: {
        $route: {
            handler(newRoute, oldRoute) {
                if (newRoute.path === oldRoute.path && !Object.keys(newRoute.query)?.length) {
                    this.viewMode = ViewMode.Grid;
                }
            }
        },

        checkboxSelectedDeliverableIds(selectedIds: string[]) {
            this.setSelectedDeliverableIds(selectedIds);
        },

        completedJobs(jobs: { name: string }[]) {
            const updatingJobs = jobs.filter(
                ({ name }: { name: string }) =>
                    JobNames.PublishXmlFeed === name ||
                    JobNames.UnpublishXmlFeed === name ||
                    JobNames.AddMetadataValue === name ||
                    JobNames.RemoveMetadataValue === name
            );
            if (updatingJobs.length) {
                this.getPaginatedDeliverables();
            }
        },

        drawerIsVisible() {
            this.urlManager?.setURLParams(
                [
                    {
                        key: UrlFilterParams.DeliverableDrawerIsVisible,
                        value: this.drawerIsVisible ? ["1"] : []
                    }
                ],
                true
            );
        },

        filterParams: {
            deep: true,
            handler(
                newFilters: { key: string; value: string[] }[],
                previousFilters: { key: string; value: string[] }[]
            ) {
                if (this.hasFilterOptionsForEachType) {
                    const filterParams = [
                        ...newFilters,
                        ...previousFilters.reduce<{ key: string; value: string[] }[]>(
                            (filtersToClear, previousFilter) => {
                                /*
                                We do this to clear previous query parameters that no
                                longer have any values, but that had previously been set.
                            */
                                const hasNewFilter =
                                    newFilters.findIndex(newFilter => newFilter.key === previousFilter.key) > -1;
                                if (!hasNewFilter) {
                                    return [
                                        ...filtersToClear,
                                        {
                                            key: previousFilter.key,
                                            value: []
                                        }
                                    ];
                                }
                                return filtersToClear;
                            },
                            []
                        )
                    ];
                    this.urlManager?.setURLParams(filterParams, this.mounted);
                    /*
                        As this is triggered on initial load of the page by setting
                        filters from URL params it could overwrite the page set by
                        URL params, as a result we guard it with a flag.
                    */
                    if (this.hasInitialisedFilterParams) {
                        this.setCurrentPage(1);
                        /*
                            If we change filters then we don't know how many pages we have
                            so we'll default to 1 while the data is loading.
                        */
                        this.pagination = {
                            ...this.pagination,
                            totalItems: this.pagination.pageSize
                        };
                    }
                    if (this.sizeFilters.length > 0) {
                        /*
                            The LibrarySizeFilter component currently sets the initial
                            state for the template/size filters, so we're not going
                            to toggle the initialised flag to true until we have size
                            filters.
                        */
                        this.hasInitialisedFilterParams = true;
                    }

                    this.mounted = false;
                    this.getPaginatedDeliverables();
                }
            }
        },

        "creativeLibraryFiltersStore.getConstructedLibraryFilters": {
            deep: true,
            handler() {
                this.getPaginatedDeliverables();
            }
        },

        isGridViewMode() {
            this.paginatedDeliverables = [];
            if (this.isGridViewMode) {
                this.creativeLibraryFiltersStore.metadataSearch = {};
            }
        },

        async isLoading() {
            if (!this.isLoading) {
                if (this.hasErrorLoadingRequiredData || !this.hasFilterOptionsForEachType) {
                    this.$store.dispatch(UiAction.CloseSidebar);
                    return;
                }
                this.$store.dispatch(UiAction.OpenSidebar);
                if (!this.isInitializedForCampaign) {
                    this.$store.dispatch(DeliverableLibraryAction.SetInitialState, this.$route.query);
                    this.creativeLibraryFiltersStore.setInitialStateFromQuery(
                        this.$store.state.deliverableLibrary.filterOptions,
                        this.$route.query
                    );
                } else {
                    /*
                        If we get here then we can assume that the library previously
                        been initialized for the campaign and we just need to update the
                        URL so that it reflects the state we have.
                    */
                    const filters = [...(this.filterParams as { key: string; value: string[] }[])];
                    this.urlManager?.setURLParams(filters, this.mounted);

                    this.setCurrentPageQueryParam();
                    this.setPageSizeQueryParam();
                    this.setViewModeQueryParam();
                }
                this.getPaginatedDeliverables();
            }
            await this.$nextTick();
            this.setBodyDimensionsPixels();
        },

        isPermissionGranted() {
            this.getMetadataFields();
        },

        hasCreativeXEnabled: {
            handler(newVal) {
                if (newVal) {
                    return;
                }

                if (this.creativeLibraryFiltersStore.creativeXStatus !== PreflightStatusFilters.All) {
                    this.creativeLibraryFiltersStore.creativeXStatus = PreflightStatusFilters.All;
                }

                if (this.creativeLibraryFiltersStore.creativeXScore !== CreativeXScoreFilters.All) {
                    this.creativeLibraryFiltersStore.creativeXScore = CreativeXScoreFilters.All;
                }
            },
            immediate: true
        },

        hasSataliaEnabled: {
            handler(newVal) {
                if (newVal) {
                    return;
                }

                if (this.creativeLibraryFiltersStore.sataliaStatus !== PreflightStatusFilters.All) {
                    this.creativeLibraryFiltersStore.sataliaStatus = PreflightStatusFilters.All;
                }
            },
            immediate: true
        }
    },

    created() {
        (window as unknown as EventTarget).addEventListener("resize", this.setBodyDimensionsPixels, true);

        // @ts-expect-error - Just ignore for the moment
        this.importExportService = new ImportExport(this.$apollo, this.$store);
        this.$store.dispatch(UiAction.SetSidebarWidthOpen, 250);

        this.urlManager = markRaw(
            new URLManager(this.$router, {
                tracked: [
                    ...Object.values(UrlFilterParams),
                    ...Object.values(UrlFilterParams).map(
                        urlFilterParam => `${QueryParamPrefix.NoValues}-${urlFilterParam}`
                    ),
                    ...Object.values(UrlFilterParams).map(
                        urlFilterParam => `${QueryParamPrefix.InactiveValues}-${urlFilterParam}`
                    )
                ]
            })
        );
        if (!this.isInitializedForCampaign) {
            this.libraryCampaignId = null;
        }
        this.getMetadataFields();
        if (this.isGridViewMode) {
            this.setPageSize(ViewModeDefaultPageSize[ViewMode.Grid]);
        } else {
            this.setPageSize(ViewModeDefaultPageSize[ViewMode.Table]);
        }
    },

    beforeUnmount() {
        (window as unknown as EventTarget).removeEventListener("resize", this.setBodyDimensionsPixels, true);
        this.isBeingDestroyed = true;
        this.onDrawerClosed();
        this.$store.dispatch(DeliverableLibraryAction.UnsetDeliverableStatusByIdHash, this.paginatedDeliverables);
    },

    mounted() {
        this.mounted = true;
    },

    methods: {
        hasMoreToSelect(selectedDeliverableIds: string[]) {
            return (
                selectedDeliverableIds.length &&
                selectedDeliverableIds.length === this.paginatedDeliverables.length &&
                selectedDeliverableIds.length !== this.pagination.totalItems
            );
        },

        async fetchAllDeliverables() {
            const allDeliverables = [];
            const maxPageSize = 100;
            const totalItems = this.pagination.totalItems;

            const totalPagesWithMaxSize = Math.ceil(totalItems / maxPageSize);

            const batchSize = 5;
            const batches = [];

            // Batch deliverables to avoid long running requests and memory issues
            for (let page = 1; page <= totalPagesWithMaxSize; page += batchSize) {
                const batch = [];
                const endPage = Math.min(page + batchSize - 1, totalPagesWithMaxSize);

                for (let currentPage = page; currentPage <= endPage; currentPage++) {
                    batch.push(
                        getPaginatedDeliverables({
                            campaignId: this.campaignId,
                            libraryFilters: this.creativeLibraryFiltersStore.getConstructedLibraryFilters,
                            page: currentPage,
                            deliverablesPerPage: maxPageSize,
                            store: this.$store
                        })
                    );
                }

                batches.push(batch);
            }

            for (const batch of batches) {
                const batchResults = await Promise.all(batch);
                for (const { deliverables } of batchResults) {
                    allDeliverables.push(...deliverables);
                }
            }

            const uniqueDeliverables = allDeliverables.filter(
                (deliverable, index, arr) => arr.findIndex(d => d.idHash === deliverable.idHash) === index
            );

            return uniqueDeliverables;
        },

        async fetchImportDeliverables(
            filters?: {
                languages: string[] | null;
                masterTemplateIds: string[];
                editableGroups:
                    | {
                          name: string;
                          valueIds: string[] | undefined;
                      }[]
                    | null;
            },
            reportingLabels?: string[]
        ) {
            const allDeliverables = [];
            const maxPageSize = 100;
            let currentPage = 1;
            let hasMore = true;

            while (hasMore) {
                const { deliverables, pagination } = await getPaginatedDeliverables({
                    campaignId: this.campaignId,
                    libraryFilters: {
                        ...this.creativeLibraryFiltersStore.getConstructedLibraryFilters,
                        filters: {
                            ...this.creativeLibraryFiltersStore.getConstructedLibraryFilters.filters,
                            ...filters
                        }
                    },
                    page: currentPage,
                    deliverablesPerPage: maxPageSize,
                    store: this.$store
                });

                allDeliverables.push(...deliverables);

                hasMore = currentPage * maxPageSize < pagination.totalItems;
                currentPage++;
            }

            const uniqueDeliverables = allDeliverables.filter((deliverable, index, arr) => {
                const isUnique = arr.findIndex(d => d.idHash === deliverable.idHash) === index;
                if (!reportingLabels || !reportingLabels.length) {
                    return isUnique;
                }
                return isUnique && reportingLabels.includes(deliverable.reportingLabel);
            });

            return uniqueDeliverables;
        },

        async getMetadataFields() {
            this.hasErrorLoadingMetadataFields = false;
            if (this.isPermissionGranted && !this.isLoadingMetadataFields) {
                this.isLoadingMetadataFields = true;
                try {
                    const metadataFields = await getMetadataFieldsOfCampaign(this.campaignId);
                    this.$store.dispatch(DeliverableLibraryAction.ClearMetadataFieldsByFieldId);
                    metadataFields.forEach(metadataField => {
                        this.$store.dispatch(DeliverableLibraryAction.SetMetadataFieldsByFieldId, metadataField);
                    });
                } catch {
                    this.hasErrorLoadingMetadataFields = true;
                }
                this.isLoadingMetadataFields = false;
            }
        },

        async getPaginatedDeliverables() {
            const getPaginatedDeliverablesCb = async () => {
                if (this.isBeingDestroyed) {
                    return;
                }
                this.hasErrorLoadingDeliverables = false;
                /*
                    While it may change in the future, we want to clear the selected deliverables
                    any time we attempt to fetch new deliverables.
                */
                this.setSelectedDeliverableIds([]);
                /*
                    Because we may have long running requests that may complete after shorter requests
                    we are going to check against a component level counter to ensure that the request
                    is the most recent before setting any state as a result of the query.
                */
                const isMostRecentRequest = (requestCount: number) =>
                    this.paginatedDeliverablesRequestCounter === requestCount &&
                    this.getPaginatedDeliverablesTimeout === undefined &&
                    !this.isBeingDestroyed;
                this.paginatedDeliverablesRequestCounter += 1;
                /*
                    So that `deliverableStatusByIdHash` is not unbounded in
                    how large it can grow we clear out the existing deliverables.
                */
                this.$store.dispatch(
                    DeliverableLibraryAction.UnsetDeliverableStatusByIdHash,
                    this.paginatedDeliverables
                );
                /*
                    It's possible for this function to be called before the page has finished
                    loading things such as editables and masterTemplates, which we require to
                    process the deliverables, so we don't want to allow fetching until they've been
                    loaded.

                    There's also no point in attempting to query without having all filters as the
                    response would contain no deliverables.
                */
                if (!this.isLoading && this.allFiltersAreActive) {
                    const requestCount = this.paginatedDeliverablesRequestCounter;
                    this.isLoadingDeliverables = true;
                    /*
                        We're going to clear the deliverables when we start to fetch new deliverables,
                        partly because it looks cleaner, but mainly because it allows us to avoid issues
                        relating to lazy loaded banners not being correctly initialised.

                        We don't do this for the table view as we want to keep the deliverables so that
                        the table columns do not instantly resize/shift out of view when filtering using
                        the metadata column search inputs.
                    */
                    if (this.isGridViewMode) {
                        this.paginatedDeliverables = [];
                    }

                    try {
                        const { deliverables, pagination } = await getPaginatedDeliverables({
                            campaignId: this.campaignId,
                            libraryFilters: this.creativeLibraryFiltersStore.getConstructedLibraryFilters,
                            page: this.pagination.currentPage,
                            deliverablesPerPage: this.pagination.pageSize,
                            store: this.$store
                        });
                        /*
                            If we have a pending request, don't bother proceeding
                            any further as it will be overwritten shortly.
                        */
                        if (!isMostRecentRequest(requestCount)) {
                            return;
                        }
                        /*
                            If somehow the user has managed to get to a page that is beyond the max page
                            we want to bump them down the last possible page to in order correct things.
                        */
                        if (this.pagination.currentPage > pagination.maxPage && pagination.totalCount > 0) {
                            this.setCurrentPage(pagination.maxPage);
                            this.pagination = {
                                ...this.pagination,
                                totalPages: pagination.maxPage,
                                totalItems: pagination.totalCount
                            };
                            return;
                        }
                        this.pagination = {
                            ...this.pagination,
                            totalPages: pagination.maxPage,
                            totalItems: pagination.totalCount
                        };
                        this.$store.dispatch(DeliverableLibraryAction.SetDeliverableStatusByIdHash, deliverables);
                        this.paginatedDeliverables = deliverables;
                    } catch {
                        if (isMostRecentRequest(requestCount)) {
                            this.hasErrorLoadingDeliverables = true;
                        }
                    } finally {
                        if (isMostRecentRequest(requestCount)) {
                            this.isLoadingDeliverables = false;
                        }
                    }
                } else {
                    this.isLoadingDeliverables = false;
                    this.paginatedDeliverables = [];
                }
            };

            if (this.isLoadingDeliverables) {
                clearTimeout(this.getPaginatedDeliverablesTimeout);
                this.getPaginatedDeliverablesTimeout = setTimeout(() => {
                    this.getPaginatedDeliverablesTimeout = undefined;
                    getPaginatedDeliverablesCb();
                }, 500);
            } else {
                getPaginatedDeliverablesCb();
            }
        },

        getRequiredLibraryData() {
            this.getMetadataFields();
            this.$apollo.queries.campaign.refetch();
            this.$apollo.queries.computedDeliverableComponents.refetch();
        },

        onDeliverableStatusUpdate() {
            this.drawerDeliverablesHadStatusUpdated = true;
        },

        onDeliverableUserStatusUpdate() {
            this.drawerDeliverablesHadUserStatusUpdated = true;
        },

        onDeliverableCommentsUpdate() {
            this.drawerDeliverablesHadCommentsUpdated = true;
        },

        onDrawerClosed() {
            /*
                So that we don't display a banner that has been approved in the QA
                drawer in the library when filtering by QA status = IN_REVIEW we
                refetch the deliverables that we are showing in the library if
                we have updated the QA status of a deliverable in the QA drawer.

                We could try to be more intelligent about this by tracking which
                statuses have been changed (from and to) and for which banners,
                but the logic overhead/complexity didn't seem worth it.

                We check if the component is being destroyed as we do not need
                to update the deliverables if it is being destroyed.
            */
            if (!this.isBeingDestroyed) {
                let shouldRefetchDeliverables = false;
                if (this.drawerDeliverablesHadStatusUpdated && this.isFilteringByStatus) {
                    this.drawerDeliverablesHadStatusUpdated = false;
                    shouldRefetchDeliverables = true;
                }

                if (this.drawerDeliverablesHadUserStatusUpdated && this.isFilteringByUserStatus) {
                    this.drawerDeliverablesHadUserStatusUpdated = false;
                    shouldRefetchDeliverables = true;
                }

                if (this.drawerDeliverablesHadCommentsUpdated && this.isFilteringByCommentedOnBy) {
                    this.drawerDeliverablesHadCommentsUpdated = false;
                    shouldRefetchDeliverables = true;
                }

                if (shouldRefetchDeliverables) {
                    this.getPaginatedDeliverables();
                }
            }

            this.drawerIsVisible = false;
            this.drawerDeliverables = null;
            this.$store.dispatch(DeliverableLibraryAction.SetPreviewDrawerActiveTab, null);
        },

        async onSelectAll() {
            this.isPaginationNoticeShown = false;
            this.isLoadingDeliverables = true;

            try {
                /*
                    So that `deliverableStatusByIdHash` is not unbounded in
                    how large it can grow we clear out the existing deliverables.
                */
                this.$store.dispatch(
                    DeliverableLibraryAction.UnsetDeliverableStatusByIdHash,
                    this.paginatedDeliverables
                );
                this.paginatedDeliverables = await this.fetchAllDeliverables();
                this.creativeLibraryFiltersStore.allSelectedMode = true;
            } catch (error) {
                console.error("Error fetching all deliverables", error);
                this.creativeLibraryFiltersStore.allSelectedMode = false;
            } finally {
                this.setSelectedDeliverableIds(this.paginatedDeliverables.map(({ idHash }) => idHash));
                this.$store.dispatch(DeliverableLibraryAction.SetDeliverableStatusByIdHash, this.paginatedDeliverables);
                this.isLoadingDeliverables = false;
            }
        },

        async applySelectMultipleDeliverables(reportingLabels: string[]) {
            this.isLoadingDeliverables = true;

            try {
                this.paginatedDeliverables = await this.fetchAllDeliverables();
            } catch (error) {
                console.error("Error fetching all deliverables", error);
            } finally {
                this.setSelectedDeliverableIds(
                    this.paginatedDeliverables.reduce((acc: string[], deliverable) => {
                        if (reportingLabels.includes(deliverable.reportingLabel)) {
                            acc.push(deliverable.idHash);
                        }
                        return acc;
                    }, [])
                );
                this.isLoadingDeliverables = false;
            }
        },

        onExportTemplate() {
            if (this.importExportService) {
                this.importExportService.exportLibrarySelectTemplate();
            } else {
                console.error("Import/Export service is not initialized");
            }
        },

        onReplayPreviews() {
            this.ga4.trackClickEvent({
                interactionObject: GA4.CLICK_INTERACTION_OBJECTS.BUTTON,
                interactionText: GA4.CLICK_INTERACTION_TEXTS.REPLAY_BANNER
            });
            this.playbackController.replay();
        },

        redirectToClients() {
            this.$router.push({ name: AppRouteNames.HOME });
        },

        refetchDeliverables() {
            this.getPaginatedDeliverables();
        },

        refetchDeliverablesIfFilteringByPublishedStatus() {
            if (this.isFilteringByPublishedStatus) {
                this.getPaginatedDeliverables();
            }
        },

        refetchDeliverablesIfFilteringByQaStatus() {
            if (this.isFilteringByStatus) {
                this.getPaginatedDeliverables();
            }
        },

        setCurrentPage(newPage: number) {
            if (newPage !== this.pagination.currentPage) {
                this.pagination = {
                    ...this.pagination,
                    currentPage: newPage
                };
                this.setCurrentPageQueryParam();
                this.getPaginatedDeliverables();
            }
        },

        setCurrentPageQueryParam() {
            this.urlManager?.setURLParams(
                [
                    {
                        key: UrlFilterParams.Page,
                        value: this.pagination.currentPage === 1 ? [] : [this.pagination.currentPage.toString()]
                    }
                ],
                this.mounted
            );
        },

        setEditableGroupFilterOptions(editableGroupValues: EditableGroupValue[]) {
            type EditableGroupOptions = Record<
                string,
                {
                    label: string;
                    name: string;
                    options: { label: string; value: string }[];
                }
            >;
            const optionsByEditableGroup = editableGroupValues.reduce<EditableGroupOptions>(
                (acc, { _id, editableGroupName, value }) => {
                    if (!acc[editableGroupName]) {
                        acc[editableGroupName] = {
                            label: formatGroupName(editableGroupName),
                            name: editableGroupName,
                            options: []
                        };
                    }
                    acc[editableGroupName].options.push({
                        label: value,
                        value: _id
                    });
                    return acc;
                },
                {}
            );
            const editableGroupsWithOptions = Object.values(optionsByEditableGroup);
            this.$store.dispatch(DeliverableLibraryAction.SetEditableGroupFilterOptions, editableGroupsWithOptions);
        },

        setMasterTemplateFilterOptions(masterTemplates: MasterTemplate[]) {
            /*
                We do this sort so that the same master templates get suffixed with
                the same number (if there are no changes to the master templates)
                by the generateLabel function if there are multiple master templates
                of the same size.
            */
            const masterTemplatesSortedById = [...masterTemplates].sort((a, b) => {
                if (a.created !== undefined && b.created !== undefined) {
                    if (a.created < b.created) {
                        return -1;
                    }
                    if (a.created > b.created) {
                        return 1;
                    }
                } else if (a.created !== undefined) {
                    return -1;
                } else if (b.created !== undefined) {
                    return 1;
                }
                return 0;
            });
            const generatedTemplateLabels = [] as string[];
            const masterTemplateOptions = [] as MasterTemplateOptions[];
            masterTemplatesSortedById.forEach(masterTemplate => {
                const label = generateTemplateLabel(masterTemplate, generatedTemplateLabels);
                generatedTemplateLabels.push(label);
                masterTemplateOptions.push({
                    height: masterTemplate.width,
                    label,
                    value: masterTemplate._id,
                    width: masterTemplate.width
                });
            });
            /*
                We sort the options by label because it's nicer if the options
                are sorted by dimensions in the UI.
            */
            const masterTemplateOptionsSortedByDimensions = [...masterTemplateOptions].sort((a, b) => {
                if (a.width < b.width) {
                    return -1;
                }
                if (a.width > b.width) {
                    return 1;
                }
                if (a.height < b.height) {
                    return -1;
                }
                if (a.height > b.height) {
                    return 1;
                }
                if (a.label < b.label) {
                    return -1;
                }
                if (a.label > b.label) {
                    return 1;
                }
                return 0;
            });
            this.$store.dispatch(
                DeliverableLibraryAction.SetMasterTemplateFilterOptions,
                masterTemplateOptionsSortedByDimensions
            );
        },

        setPageSize(newPageSize: number) {
            if (newPageSize !== this.pagination.pageSize) {
                this.pagination = {
                    ...this.pagination,
                    pageSize: newPageSize,
                    /*
                        If we change the page size then we don't know how many pages we have
                        so we'll default to 1 while the data is loading. We do this by setting
                        the total number of items to the new page size.
                    */
                    totalItems: newPageSize
                };
                this.setPageSizeQueryParam();
                this.setCurrentPage(1);
                this.getPaginatedDeliverables();
            }
        },

        setScalingFactor(scalingFactor: number) {
            this.ga4.trackClickEvent({
                interactionObject: GA4.CLICK_INTERACTION_OBJECTS.DROPDOWN,
                interactionText: `${GA4.CLICK_INTERACTION_TEXTS.AD_SCALING} - ${scalingFactor}`
            });
            this.scalingFactor = scalingFactor;
            this.setScalePageSize();
        },

        setScalePageSize() {
            switch (this.scalingFactor) {
                case 1:
                    this.setPageSize(10);
                    break;
                case 0.75:
                    this.setPageSize(20);
                    break;
                case 0.5:
                    this.setPageSize(30);
                    break;
                case 0.25:
                    this.setPageSize(40);
                    break;
                default:
                    this.setPageSize(10);
            }
        },

        setPageSizeQueryParam() {
            const shouldSetQueryParam =
                (this.viewMode === ViewMode.Grid &&
                    this.pagination.pageSize !== ViewModeDefaultPageSize[ViewMode.Grid]) ||
                (this.viewMode === ViewMode.Table &&
                    this.pagination.pageSize !== ViewModeDefaultPageSize[ViewMode.Table]);
            this.urlManager?.setURLParams(
                [
                    {
                        key: UrlFilterParams.PageSize,
                        value: shouldSetQueryParam ? [this.pagination.pageSize.toString()] : []
                    }
                ],
                this.mounted
            );
        },

        async downloadDeliverableIdsFromFile(
            selectedDeliverableIdentifiers: DeliverableIdentifier[],
            reportingLabels: string[]
        ) {
            try {
                this.isDownloadDeliveriesLoading = true;
                this.isImportDownload = true;

                const { uniqueLanguages, uniqueMasterTemplateIds, uniqueGroupValuesByGroupName } =
                    this.getImportSelectionFilters(selectedDeliverableIdentifiers);

                const editableGroups = Object.entries(uniqueGroupValuesByGroupName).map(([name, values]) => ({
                    name,
                    valueIds: values
                }));

                this.downloadDeliverables = await this.fetchImportDeliverables(
                    {
                        languages: uniqueLanguages,
                        masterTemplateIds: uniqueMasterTemplateIds,
                        editableGroups
                    },
                    reportingLabels
                );

                if (!this.downloadDeliverables.length) {
                    this.$snackbar.error("No deliverables found matching the imported file");
                    return;
                }

                this.toggleDownloadModal();
            } catch {
                this.toggleImportExportModalVisible();
            } finally {
                this.isDownloadDeliveriesLoading = false;
            }
        },

        applySelectionFromFile(selectedDeliverableIdentifiers: DeliverableIdentifier[], reportingLabels: string[]) {
            this.selectedDeliverableIds = [];
            this.checkboxSelectedDeliverableIds = [];

            const { uniqueLanguages, uniqueMasterTemplateIds, uniqueGroupValuesByGroupName } =
                this.getImportSelectionFilters(selectedDeliverableIdentifiers);

            this.creativeLibraryFiltersStore.selectedLanguages = uniqueLanguages;
            this.creativeLibraryFiltersStore.selectedSizes = uniqueMasterTemplateIds;
            this.creativeLibraryFiltersStore.selectedEditableIdsByGroup = uniqueGroupValuesByGroupName;

            this.toggleImportExportModalVisible();

            if (reportingLabels.length) {
                this.applySelectMultipleDeliverables(reportingLabels);
            }
        },

        getImportSelectionFilters(selectedDeliverableIdentifiers: DeliverableIdentifier[]) {
            const uniqueLanguages = [...new Set(selectedDeliverableIdentifiers.map(item => item.language))];
            const uniqueMasterTemplateIds = [
                ...new Set(selectedDeliverableIdentifiers.map(item => item.masterTemplateId))
            ];

            const allEditableGroupValueIds = selectedDeliverableIdentifiers.flatMap(item => item.editableGroupValueIds);
            const allGroupNamesAndValues = this.getGroupNamesAndValuesByIds(allEditableGroupValueIds);

            const uniqueGroupValuesByGroupName: Record<string, string[]> = {};
            for (const group of allGroupNamesAndValues) {
                if (group) {
                    if (!uniqueGroupValuesByGroupName[group.groupName]) {
                        uniqueGroupValuesByGroupName[group.groupName] = [];
                    }
                    if (!uniqueGroupValuesByGroupName[group.groupName]?.includes(group.value)) {
                        uniqueGroupValuesByGroupName[group.groupName]?.push(group.value);
                    }
                }
            }

            return {
                uniqueLanguages,
                uniqueMasterTemplateIds,
                uniqueGroupValuesByGroupName
            };
        },

        getGroupNameAndValueById(editableGroupValueId: string) {
            for (const [groupName, groupData] of Object.entries(this.validGroupValuesNamesByGroupValue)) {
                const valueName = Object.keys(groupData.editableGroupValuesNamesMap).find(
                    key => groupData.editableGroupValuesNamesMap[key] === editableGroupValueId
                );

                if (valueName) {
                    return {
                        groupName,
                        value: editableGroupValueId
                    };
                }
            }

            return null;
        },

        getGroupNamesAndValuesByIds(editableGroupValueIds: string[]) {
            return editableGroupValueIds.map(id => this.getGroupNameAndValueById(id)).filter(Boolean);
        },

        setSelectedDeliverableIds(selectedDeliverableIds: string[]) {
            if (selectedDeliverableIds.length !== this.paginatedDeliverables.length) {
                this.creativeLibraryFiltersStore.allSelectedMode = false;
            }

            if (this.hasMoreToSelect(selectedDeliverableIds)) {
                this.isPaginationNoticeShown = true;
            }

            if (!selectedDeliverableIds.length && this.isPaginationNoticeShown) {
                this.isPaginationNoticeShown = false;
            }

            this.selectedDeliverableIds = selectedDeliverableIds;
            this.checkboxSelectedDeliverableIds = selectedDeliverableIds;
        },

        setViewMode(viewMode: (typeof ViewMode)[keyof typeof ViewMode]) {
            if (viewMode !== this.viewMode) {
                this.viewMode = viewMode;
                /*
                    The following logic was originally planned to be triggered by a
                    watcher, however that caused some complicated sequence and side effect
                    related issues where pageSize and currentPage were being set, overwriting
                    values that had been set from URL query params.

                    This also applies to setPageSize().
                */
                if (this.viewMode === ViewMode.Grid) {
                    this.setPageSize(ViewModeDefaultPageSize[ViewMode.Grid]);
                } else {
                    this.setPageSize(ViewModeDefaultPageSize[ViewMode.Table]);
                }
                this.setCurrentPage(1);
                this.setViewModeQueryParam();
                this.setSelectedDeliverableIds([]);
                this.getPaginatedDeliverables();
            }
        },

        setViewModeQueryParam() {
            this.urlManager?.setURLParams(
                [
                    {
                        key: UrlFilterParams.ViewMode,
                        value: this.viewMode === ViewMode.Grid ? [] : [this.viewMode]
                    }
                ],
                this.mounted
            );
        },

        showBulkApproval() {
            if (this.allSelectedModeEnabled) {
                this.$store.dispatch(DeliverableLibraryAction.SetDrawerDeliverables, null);
            } else {
                this.$store.dispatch(
                    DeliverableLibraryAction.SetDrawerDeliverables,
                    this.selectedDeliverableIds.map(idHash => this.deliverablesByIdHash[idHash])
                );
            }

            this.$store.dispatch(DeliverableLibraryAction.SetDrawerIsVisible, true);
            this.$store.dispatch(DeliverableLibraryAction.SetDrawerActiveTab, LibraryDrawerTab.QA);
        },

        toggleSharePreviewModal() {
            this.sharePreviewModalIsVisible = !this.sharePreviewModalIsVisible;
        },

        toggleDownloadModal() {
            this.downloadModalIsVisible = !this.downloadModalIsVisible;
            if (this.importExportModalVisible) {
                this.importExportModalVisible = false;
            }

            if (!this.downloadModalIsVisible) {
                this.isImportDownload = false;
                this.downloadDeliverables = [];
            }
        },

        toggleImportExportModalVisible() {
            this.importExportModalVisible = !this.importExportModalVisible;
            if (!this.importExportModalVisible) {
                this.selectedDeliverableIdentifiers = [];
            }
        },

        togglePublishModal(platformAction: { platform: keyof typeof PlatformLabel; action: "publish" | "unpublish" }) {
            if (this.publishModalIsVisible) {
                this.publishModalData = {};
            } else {
                this.publishModalData = platformAction;
            }
            this.publishModalIsVisible = !this.publishModalIsVisible;
        },

        updateMetadataValueOnDeliverables(idHashes: string[], updatedMetadataValue: DeliverableMetadataValue) {
            this.paginatedDeliverables = this.paginatedDeliverables.map(existingDeliverable => {
                if (idHashes.includes(existingDeliverable.idHash)) {
                    const updatedMetadataByFieldId: Record<string, DeliverableMetadataValue> = {};
                    // store a ref to each current metadataValue indexed by fieldId
                    (existingDeliverable.metadata as DeliverableMetadataValue[]).forEach(metadata => {
                        updatedMetadataByFieldId[metadata.field._id] = metadata;
                    });
                    // if the value coming through is falsey then it is being removed
                    if (!updatedMetadataValue.value) {
                        delete updatedMetadataByFieldId[updatedMetadataValue.field._id];
                    } else {
                        // otherwise overwrite/create the new value for the fieldId
                        updatedMetadataByFieldId[updatedMetadataValue.field._id] = updatedMetadataValue;
                    }

                    return {
                        ...existingDeliverable,
                        metadata: Object.values(updatedMetadataByFieldId)
                    };
                }
                return existingDeliverable;
            });
        },

        updateReportingLabelOnDeliverable(ev: { idHash: string; reportingLabel: string }) {
            const { idHash, reportingLabel } = ev;
            this.paginatedDeliverables = this.paginatedDeliverables.map(existingDeliverable => {
                if (idHash === existingDeliverable.idHash) {
                    return {
                        ...existingDeliverable,
                        reportingLabel
                    };
                }
                return existingDeliverable;
            });
        },

        campaignUnlocked() {
            this.$apollo.queries.getLastJobByCampaignId.refetch();
        }
    },

    apollo: {
        campaign: {
            query: campaignLibraryQuery,
            fetchPolicy: "network-only",
            loadingKey: "isLoadingCampaign",
            variables() {
                return {
                    campaignId: this.campaignId
                };
            },

            skip() {
                return !this.isPermissionGranted;
            },

            async result({ data, error }) {
                if (error) {
                    this.hasErrorLoadingCampaign = true;
                    return;
                }
                this.hasErrorLoadingCampaign = false;
                this.$store.dispatch(CampaignAction.SetCampaign, data.campaign);
                this.$store.dispatch(CampaignAction.SetMasterTemplates, data.masterTemplatesByCampaign);
                this.$store.dispatch(CampaignAction.SetGroups, data.editableGroupsByCampaign);

                // make sure that the changes made in editor are reflected in filters
                this.creativeLibraryFiltersStore.syncFilters({
                    languages: this.$store.state.campaign.languages,
                    editableGroupValueIds: this.$store.state.campaign.editableGroupValueIds,
                    masterTemplateIds: this.$store.state.campaign.normalized.masterTemplateIds
                });
            }
        },

        computedDeliverableComponents: {
            query: computedDeliverableComponentsQuery,
            fetchPolicy: "network-only",
            loadingKey: "isLoadingLibraryFilterOptions",
            variables() {
                return {
                    campaignId: this.campaignId
                };
            },

            skip() {
                return !this.isPermissionGranted;
            },

            result({ data, error }) {
                if (error) {
                    this.hasErrorLoadingFilterOptions = true;
                    return;
                }
                this.hasErrorLoadingFilterOptions = false;
                const { deliverableCount, editableGroupValues, languages, masterTemplates } =
                    data.computedDeliverableComponents;
                this.numberOfDeliverablesThatUserCanView = deliverableCount;
                this.setEditableGroupFilterOptions(editableGroupValues);
                this.$store.dispatch(DeliverableLibraryAction.SetLanguageFilterOptions, languages);
                this.setMasterTemplateFilterOptions(masterTemplates);
            }
        },

        editablesWithOverwritesByCampaign: {
            query: campaignEditorQuery,
            fetchPolicy: "no-cache",
            variables() {
                return {
                    campaignId: this.campaignId
                };
            },

            skip() {
                return !this.isPermissionGranted;
            },

            result({ data }) {
                if (data.editablesWithOverwritesByCampaign) {
                    this.editableOverwrites = data.editablesWithOverwritesByCampaign;
                }
            }
        },

        getLastJobByCampaignId: {
            query: lastJobByCampaignId,
            fetchPolicy: "network-only",
            loadingKey: "isLoadingLastCampaignJob",
            variables() {
                return {
                    campaignId: this.campaignId
                };
            },

            skip() {
                return !this.isPermissionGranted;
            },

            async result({ data, error }) {
                if (error) {
                    this.hasErrorLoadingLastCampaignJob = true;
                    return;
                }

                this.hasErrorLoadingLastCampaignJob = false;

                if (data?.getLastJobByCampaignId?.job) {
                    this.lastJob = data.getLastJobByCampaignId.job;
                    this.isLastJobComplete = data.getLastJobByCampaignId.job.status === JobStatus.COMPLETE;
                }

                this.campaignFeed = data?.getLastJobByCampaignId?.feed ?? [];
            }
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.campaign-library__wrapper {
    height: 100%;
    position: relative;
    width: 100%;
}

.campaign-library {
    bottom: 0;
    display: flex;
    flex-direction: column;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;

    &__body {
        flex: 1;
        overflow-y: auto;
        padding: $spacing-large 20px $spacing;
        position: relative;

        &--no-padding {
            padding: 0;
        }

        &--no-scroll {
            overflow-y: hidden;
        }
    }

    &__empty-message-wrapper {
        padding: $spacing-large 0 0;
    }

    &__loading-error-message-wrapper {
        padding: 0 $spacing-large;
    }

    &__select-all-notice-wrapper {
        border-bottom: 1px solid $grey3;
        padding: $spacing 20px 0;
    }

    &__variations {
        color: $cmp-light-active;
        font-weight: bold;
        height: $campaign-nav-height;
        display: flex;
        justify-content: center;
        align-items: center;
        border-bottom: 1px solid $grey3;
    }
}

.library-previews-container {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;

    // width: 100%;
    // height: 100%;
}

.campaign-edit__deliverables-size-group {
    width: 100%;

    display: flex;
    flex-wrap: wrap;
    gap: 15px;
}

.campaign-edit__deliverables-size-group--single {
    width: inherit;
}

.campaign-edit__deliverable {
    vertical-align: top;
    flex-grow: 0;
    flex-shrink: 0;
}

.campaign-library__sidebar-nav-buttons-group {
    width: 100%;
    display: flex;

    .campaign-library__sidebar-nav-button {
        &.ivu-btn-primary,
        &.ivu-btn,
        &.ivu-btn-default {
            flex: 1;
            padding: 9px 2px !important;
            margin: 0 !important;
            height: auto !important;
        }
    }
}
</style>
